import type { MultiTenantNetworkOrganizationEntity } from '../../normalization/graphql/normalizeMultiTenantOrganization.types';
import type { MultiTenantOrganizationNetwork } from '../../state/multiTenantOrganizationNetworks/types';
import type { EntityStatus, YammerState } from '../../state/types';
import { getStatus } from '../../state/types';
import { getExperimentTreatment } from '../experiments/selectors';
import { createFindById } from '../findById';
import { getCurrentNetwork } from '../networks/selectors';
import { getCurrentNetworkId } from '../session/selectors';

export const findById = createFindById('multiTenantOrganizationNetworks');

type GetMultiTenantOrganizationConnectionsForHubNetwork = (
  state: YammerState
) => MultiTenantOrganizationNetwork[] | undefined;
export const getMultiTenantNetworks: GetMultiTenantOrganizationConnectionsForHubNetwork = (state) => {
  if (!getIsMtoEnabled(state)) {
    return;
  }

  const fulfilledNetworks = state.multiTenantOrganizationNetworks.fulfilled.byId;

  return Object.values(fulfilledNetworks);
};

type GetMultiTenantOrganizationStatus = (state: YammerState) => EntityStatus;
export const getMultiTenantOrganizationStatus: GetMultiTenantOrganizationStatus = (state) =>
  getStatus(state.multiTenantOrganization);

type GetMultiTenantOrganization = (state: YammerState) => MultiTenantNetworkOrganizationEntity | undefined;
export const getMultiTenantOrganization: GetMultiTenantOrganization = (state) =>
  state?.multiTenantOrganization?.fulfilled;

type GetIsMtoEnabled = (state: YammerState) => boolean;
export const getIsMtoEnabled: GetIsMtoEnabled = (state) => {
  const isMtoEnabled = () => getExperimentTreatment(state, 'MultiTenantOrganization') === true;

  return isMtoEnabled();
};

type getIsPermalinkDeeplinkEnabled = (state: YammerState) => boolean;
export const getIsPermalinkDeeplinkEnabled: getIsPermalinkDeeplinkEnabled = (state) =>
  getExperimentTreatment(state, 'EnablePermalinkDeeplink') === true;

type GetCurrentMtoHubNetwork = (state: YammerState) => MultiTenantOrganizationNetwork | null | undefined;
export const getCurrentMtoHubNetwork: GetCurrentMtoHubNetwork = (state) =>
  getMultiTenantNetworks(state)?.find((network) => network.isHubNetwork) ?? null;

type GetCurrentMtoNetwork = (state: YammerState) => MultiTenantOrganizationNetwork | null | undefined;
export const getCurrentMtoNetwork: GetCurrentMtoNetwork = (state) => {
  const { id: currentNetworkId } = getCurrentNetwork(state);

  return findById(state, currentNetworkId) ?? null;
};

type GetIsMtoAvailable = (state: YammerState) => boolean;
export const getIsMtoAvailable: GetIsMtoAvailable = (state) => getMultiTenantOrganization(state) !== undefined;

type GetIsMtoSpokeNetworkAvailable = (state: YammerState) => boolean;
export const getIsMtoSpokeNetworkAvailable: GetIsMtoSpokeNetworkAvailable = (state) => {
  const networks = getMultiTenantNetworks(state);
  if (!networks) {
    return false;
  }

  return networks.some((network) => !network.isHubNetwork);
};

type GetIsCurrentNetworkSpoke = (state: YammerState) => boolean;
export const getIsCurrentNetworkSpoke: GetIsCurrentNetworkSpoke = (state) => {
  const currentNetworkId = getCurrentNetworkId(state);
  const hubNetworkId = getCurrentMtoHubNetwork(state)?.id;

  return currentNetworkId !== hubNetworkId;
};

type GetMultiTenantNetworksCount = (state: YammerState) => number;
export const getMultiTenantNetworksCount: GetMultiTenantNetworksCount = (state) =>
  getMultiTenantNetworks(state)?.length ?? 0;

type GetMultiTenantMemberNetworksCount = (state: YammerState) => number;
export const getMultiTenantMemberNetworksCount: GetMultiTenantMemberNetworksCount = (state) => {
  const mtoNetworksCount = getMultiTenantNetworksCount(state);

  return mtoNetworksCount > 0 ? mtoNetworksCount - 1 : 0;
};

type getCanAccessMultiTenantOrganizationGroups = (state: YammerState) => boolean;
export const getCanAccessMultiTenantOrganizationGroups: getCanAccessMultiTenantOrganizationGroups = (state) =>
  getMultiTenantOrganization(state)?.canAccessMultiTenantOrganizationGroups ?? false;
